import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import Chart from "chart.js/auto";

export default function Dashboard() {
  const [data, setData] = useState({
    poSummary: {},
    grnSummary: {},
    poTypeChart: {},
    grnTrend: {},
    poVsGrn: {},
    vendorStats: [],
    qtyStats: {},
    alerts: {},
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/dashboard-po-grn`
      );
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error(err);
    }
  };

  // ================= CARDS =================
  const cards = [
    { label: "Total PO", value: data.poSummary.total || 0 },
    { label: "Verified PO", value: data.poSummary.verified || 0 },
    { label: "Pending PO", value: data.poSummary.pending || 0 },
    { label: "Closed PO", value: data.poSummary.closed || 0 },
    { label: "Total GRN", value: data.grnSummary.total || 0 },
    { label: "GRN Today", value: data.grnSummary.today || 0 },
    { label: "Delayed PO 🚨", value: data.alerts.delayed || 0 },
  ];

  // ================= CHARTS =================
  const poTypeData = {
    labels: ["Fresh", "LSL"],
    datasets: [
      {
        data: [
          data.poTypeChart?.fresh || 0,
          data.poTypeChart?.lsl || 0,
        ],
        backgroundColor: ["#4caf50", "#ff9800"],
      },
    ],
  };

  const grnTrendData = {
    labels: data.grnTrend?.labels || [],
    datasets: [
      {
        label: "GRN",
        data: data.grnTrend?.values || [],
        borderColor: "#2196f3",
        backgroundColor: "rgba(33,150,243,0.3)",
      },
    ],
  };

  const poVsGrnData = {
    labels: data.poVsGrn?.labels || [],
    datasets: [
      {
        label: "PO Created",
        data: data.poVsGrn?.po || [],
        backgroundColor: "#9c27b0",
      },
      {
        label: "GRN Received",
        data: data.poVsGrn?.grn || [],
        backgroundColor: "#4caf50",
      },
    ],
  };

  const qtyData = {
    labels: ["Expected", "Received"],
    datasets: [
      {
        data: [
          data.qtyStats?.expected || 0,
          data.qtyStats?.received || 0,
        ],
        backgroundColor: ["#ff9800", "#4caf50"],
      },
    ],
  };

  const vendorChart = {
    labels: data.vendorStats?.map(v => v.vendor) || [],
    datasets: [
      {
        label: "PO Count",
        data: data.vendorStats?.map(v => v.count) || [],
        backgroundColor: "#3f51b5",
      },
    ],
  };

  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,

    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 20,
          font: {
            size: 12,
            weight: "600",
          },
        },
      },
    },
  };

  const lineChartOptions = {
    ...commonChartOptions,

    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
        grid: {
          color: "rgba(0,0,0,0.05)",
        },
      },

      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const barChartOptions = {
    ...commonChartOptions,

    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)",
        },
      },

      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <Box className="dashboard">

      <Typography variant="h4" className="title">
        Dashboard
      </Typography>

      {/* ================= CARDS ================= */}
      <div className="cards">
        {cards.map((c, i) => (
          <div key={i} className="card">
            <div className="label">{c.label}</div>
            <div className="value">{c.value}</div>
          </div>
        ))}
      </div>

      {/* ================= TOP CHARTS ================= */}
      <div className="charts-row">
        <div className="chart-box">
          <h3>PO Type</h3>
          <div className="chart-wrapper small-chart">
            <Doughnut
              data={poTypeData}
              options={commonChartOptions}
            />
          </div>
        </div>

        <div className="chart-box">
          <h3>GRN Trend</h3>
          <div className="chart-wrapper">
            <Line
              data={grnTrendData}
              options={lineChartOptions}
            />
          </div>
        </div>
      </div>

      {/* ================= SECOND ROW ================= */}
      <div className="charts-row">
        <div className="chart-box">
          <h3>PO vs GRN</h3>
          <div className="chart-wrapper">
            <Bar
              data={poVsGrnData}
              options={barChartOptions}
            />
          </div>
        </div>

        <div className="chart-box">
          <h3>Quantity (Expected vs Received)</h3>
          <div className="chart-wrapper small-chart">
            <Doughnut
              data={qtyData}
              options={commonChartOptions}
            />
          </div>
        </div>
      </div>

      {/* ================= VENDOR ================= */}
      <div className="full-width">
        <div className="chart-box">
          <h3>Top Vendors (PO Count)</h3>
          <div className="chart-wrapper">
            <Bar
              data={vendorChart}
              options={barChartOptions}
            />
          </div>
        </div>
      </div>

      {/* ================= ALERTS ================= */}
      <div className="alerts">
        <h3>⚠️ Alerts</h3>
        <p>Delayed PO: {data.alerts.delayed || 0}</p>
      </div>

      {/* ================= STYLES ================= */}
      <style jsx>{`
        .dashboard {
          padding: 20px;
          background: #ecf0f3;
        }

        .title {
          margin-bottom: 20px;
          font-weight: bold;
        }

        .cards {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 30px;
        }

        .card {
          flex: 1;
          min-width: 180px;
          background: white;
          padding: 20px;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          transition: 0.3s;
        }

        .card:hover {
          transform: translateY(-5px);
        }

        .label {
          font-size: 14px;
          color: #777;
        }

        .value {
          font-size: 26px;
          font-weight: bold;
          margin-top: 10px;
        }

        .charts-row {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }

        .chart-box {
          flex: 1;
          min-width: 400px;
          background: white;
          padding: 20px;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }

        .full-width {
          margin-bottom: 30px;
        }

        .alerts {
          background: #fff3cd;
          padding: 20px;
          border-radius: 10px;
          border-left: 5px solid #ff9800;
        }
      `}</style>
    </Box>
  );
}